
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
      this.loadMoreButton = this.querySelector('.btn--load-more');
      this.paginationType = this.getAttribute('data-type');
      this.countItem = parseInt(this.productList.getAttribute('data-products-count'), 10);
      this.currentPage = 2;
      this.usesNextPage = this.hasAttribute('data-next-page');
      this.nextPageUrl = this.dataset.nextPage || null;
      this.scrollObserver = null;
    }

    connectedCallback() {
      this.paginationType === 'load-more-button'
        ? this.setupLoadMoreButton()
        : this.paginationType === 'infinite-scroll'
        ? this.setupInfiniteScroll()
        : null;

      this.restoreScrollState();
      this.bindProductLinks();
    }

    bindProductLinks() {
      this.productList?.querySelectorAll('.grid__item a').forEach(link => {
        link.addEventListener('click', () => {
          sessionStorage.setItem('pagination-scrollY', window.scrollY);
          sessionStorage.setItem('pagination-currentPage', this.currentPage - 1); 
        });
      });
    }

    setupLoadMoreButton() {
      this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());
    }

    setupInfiniteScroll() {
      this.scrollObserver = new IntersectionObserver(
        entries => entries[0].isIntersecting && this.loadNextPage(),
        { threshold: [0, 1] }
      );
      this.scrollObserver.observe(this);
    }

    buildUrlWithPageNumber(search = '', key = 'page') {
      const urlParams = new URLSearchParams(search);
      urlParams.set(key, this.currentPage);
      return `?${urlParams.toString()}`;
    }

    async loadNextPage() {
      if (this.hasAttribute('loading')) return false;
      if (this.usesNextPage && !this.nextPageUrl) {
        this.finalizePagination();
        return false;
      }

      try {
        this.setAttribute('loading', true);
        this.toggleLoadingSpinner(true);

        const url = this.usesNextPage
          ? new URL(this.nextPageUrl, document.location.href).href
          : `${document.location.pathname}${this.buildUrlWithPageNumber(document.location.search)}`;
        const response = await fetch(url);
        if (this.usesNextPage && !response.ok) throw new Error(`Pagination HTTP ${response.status}`);
        const html = await response.text();

        this.renderFetchedProducts(html);
        this.currentPage++;
        return true;
      } catch (error) {
        console.error('Error fetching products:', error);
        return false;
      } finally {
        this.toggleLoadingSpinner(false);
        this.removeAttribute('loading');
      }
    }

    renderFetchedProducts(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newProductGrid = parsed.getElementById('product-grid');

      if (this.usesNextPage) {
        if (!newProductGrid) throw new Error('Missing collection grid');
        newProductGrid.querySelectorAll('.grid__item')
          .forEach(product => this.productList.appendChild(product));
        const next = Array.from(parsed.querySelectorAll('custom-pagination'))
          .find(element => element.dataset.section === this.sectionId);
        this.nextPageUrl = next?.dataset.nextPage || null;
        this.dataset.nextPage = this.nextPageUrl || '';
        if (!this.nextPageUrl) this.finalizePagination();
        this.bindProductLinks();
        return;
      }

      if (!newProductGrid?.children.length) {
        this.finalizePagination();
        return;
      }

      newProductGrid.querySelectorAll('.grid__item')
        .forEach(product => this.productList.appendChild(product));

      if (this.productList.children.length >= this.countItem) {
        this.finalizePagination();
      }


      this.bindProductLinks();
    }

    toggleLoadingSpinner(isLoading) {
      if (!this.loadMoreButton) return;

      const spinner = this.loadMoreButton.querySelector('.loading__spinner');
      this.loadMoreButton.classList.toggle('loading', isLoading);
      spinner?.classList.toggle('hidden', !isLoading);
    }

    finalizePagination() {
      this.scrollObserver?.unobserve(this);
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.classList.add('visually-hidden');
      if (this.usesNextPage && this.loadMoreButton) {
        this.loadMoreButton.disabled = true;
        this.loadMoreButton.tabIndex = -1;
      }
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.loadMoreButton?.removeEventListener('click', this.loadNextPage);
    }

    async restoreScrollState() {
      const savedPage = parseInt(sessionStorage.getItem('pagination-currentPage'), 10);
      const savedScrollY = parseInt(sessionStorage.getItem('pagination-scrollY'), 10);

      if (!isNaN(savedPage) && savedPage >= 2) {
        await this.loadPagesUpTo(savedPage);

        window.setTimeout(() => {
          window.scrollTo(0, savedScrollY);
        }, 100);
        sessionStorage.removeItem('pagination-currentPage');
        sessionStorage.removeItem('pagination-scrollY');
      }
    }

    async loadPagesUpTo(page) {
      while (this.currentPage <= page && (!this.usesNextPage || this.nextPageUrl)) {
        const loaded = await this.loadNextPage();
        if (this.usesNextPage && loaded === false) break;
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
